import {
  type CareOrder,
  CopyButton,
  type FormControlSizes,
  PricingModeEnum,
  type Quote,
  type QuoteMarket,
  SideEnum,
  toBig,
  Tooltip,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../../hooks';

interface StFCopyQuotePriceButtonProps {
  careOrder: CareOrder | undefined;
  quote: Quote;
  quoteMarket: QuoteMarket | undefined;
  size?: FormControlSizes;
  testID?: string;
}

export const StFCopyQuotePriceButton = ({
  careOrder,
  quote,
  quoteMarket,
  size,
  testID,
}: StFCopyQuotePriceButtonProps) => {
  const copyText = useMemo(() => {
    if (quote.PricingMode !== PricingModeEnum.SpreadToFixing) {
      return undefined;
    }

    const bidOrOffer = careOrder?.Side === SideEnum.Buy ? quoteMarket?.OfferPx : quoteMarket?.BidPx;
    const px = toBig(bidOrOffer)?.times(10_000).toFixed();

    return getStFQuotePriceCopyText({
      unitQty: careOrder?.units,
      fund: careOrder?.fund,
      px,
      side: careOrder?.Side,
      settleOffset: careOrder?.Annotations?.SettleOffset,
    });
  }, [careOrder, quote, quoteMarket]);

  const { enableTakerCopyQuoteFundTradeDetails } = useFeatureFlag();
  if (!enableTakerCopyQuoteFundTradeDetails) {
    return null;
  }

  if (!copyText) {
    // We weren't able to calculate any copy text, meaning we don't support whatever mode we're in, so don't render the button
    return null;
  }

  return (
    <Tooltip tooltip="Copy quote pricing and details">
      <CopyButton value={copyText} size={size} data-testid={testID} />
    </Tooltip>
  );
};

export function getStFQuotePriceCopyText({
  unitQty,
  side,
  fund,
  px,
  settleOffset,
}: {
  // Make the properties be | undefined just so its easier to use for the consumer
  unitQty: string | undefined;
  side: SideEnum | undefined;
  fund: string | undefined;
  px: string | undefined;
  settleOffset: string | undefined;
}): string | undefined {
  const pxBig = toBig(px);
  if (!fund || !pxBig || !unitQty || !side) {
    return undefined;
  }

  // 0 gets "-", positive gets "+". The number also has a "-" if its negative so don't do anything for that
  const pxSign = pxBig.eq(0) && side === SideEnum.Sell ? '-' : pxBig.lt(0) ? '' : '+';
  const offset = settleOffset != null ? ` T+${settleOffset}` : '';
  return `For ${unitQty}u ${fund} ${
    side === SideEnum.Buy ? 'CR' : 'RD'
  }${offset}, we can do NAV${pxSign}${pxBig.toFixed()}bps`;
}
